import { type FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Grid, Stack } from "@mui/material";
import {
  useAdminDashboardQuery,
  useAdminDashboardTodayQuery,
  useAdminUnpaidInvoicesQuery,
} from "@/admin/entities/adminDashboard";
import {
  DashboardHeader,
  ExpensesByCategoryTable,
  FinancialSummaryCard,
  MetricCards,
  MoneyChart,
  OrganizationsTable,
  PeriodPicker,
  SalesRevenueShareChart,
  UnpaidInvoicesTable,
  ViewsChart,
  buildFinancialSummaryRows,
  buildMetricCards,
  formatPeriodCaption,
  groupFinancialSummaryRows,
  parsePeriod,
  periodToSearchParams,
  summarizeUnpaid,
  toQueryVariables,
  type DashboardChartProps,
  type DashboardPeriod,
} from "@/admin/features/adminDashboard";
import { OpenSupportRequestsCard } from "@/admin/features/supportRequests";

/**
 * Главная админки (спека §3): шапка с периодом, финансовая сводка во всю
 * ширину, восемь карточек, три графика и три таблицы. Период живёт в
 * адресе, поэтому ссылку можно переслать и перезагрузка не сбрасывает
 * выбор. Значения «на сегодня» и неоплаченные счета отдельными
 * запросами: смена периода их не перезапрашивает.
 */
export const AdminDashboard: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => new Date(), []);
  const period = useMemo(
    () => parsePeriod(searchParams, today),
    [searchParams, today]
  );
  const variables = useMemo(() => toQueryVariables(period), [period]);

  const dashboardQuery = useAdminDashboardQuery(variables);
  const todayQuery = useAdminDashboardTodayQuery();
  const unpaidQuery = useAdminUnpaidInvoicesQuery();

  // При новых переменных Apollo отдаёт data undefined: пока идёт запрос,
  // показываем прошлый период из previousData с полосой загрузки. После
  // ошибки previousData не берём, иначе старые цифры встанут под новый период.
  const stale = !dashboardQuery.data && dashboardQuery.loading;
  const dashboard =
    (dashboardQuery.data ?? (stale ? dashboardQuery.previousData : undefined))
      ?.adminDashboard ?? null;
  const refreshing = stale && Boolean(dashboard);
  const todayData = todayQuery.data?.adminDashboardToday ?? null;
  const unpaidRows = useMemo(
    () => unpaidQuery.data?.adminUnpaidInvoices ?? [],
    [unpaidQuery.data]
  );
  const unpaid = useMemo(
    () => (unpaidQuery.data ? summarizeUnpaid(unpaidRows) : null),
    [unpaidQuery.data, unpaidRows]
  );

  const ready = dashboard && todayData && unpaid;
  const cards = useMemo(
    () =>
      dashboard && todayData && unpaid
        ? buildMetricCards({ dashboard, today: todayData, unpaid })
        : null,
    [dashboard, todayData, unpaid]
  );
  const summaryRows = useMemo(
    () =>
      dashboard && todayData && unpaid
        ? buildFinancialSummaryRows({
            current: dashboard.current,
            previous: dashboard.previous,
            unpaid,
            today: todayData,
          })
        : null,
    [dashboard, todayData, unpaid]
  );
  const summaryGroups = useMemo(
    () => (summaryRows ? groupFinancialSummaryRows(summaryRows) : null),
    [summaryRows]
  );

  const dashboardLoading = dashboardQuery.loading && !dashboard;
  const dashboardError = Boolean(dashboardQuery.error);
  const anyLoading =
    dashboardLoading ||
    (todayQuery.loading && !todayData) ||
    (unpaidQuery.loading && !unpaidQuery.data);
  const anyError =
    dashboardError || Boolean(todayQuery.error) || Boolean(unpaidQuery.error);

  const retryDashboard = () => {
    void dashboardQuery.refetch();
  };
  const retryUnpaid = () => {
    void unpaidQuery.refetch();
  };
  const retryAll = () => {
    retryDashboard();
    void todayQuery.refetch();
    retryUnpaid();
  };

  // replace: каждый клик по периоду не должен плодить запись в истории.
  const onPeriodChange = (next: DashboardPeriod) =>
    setSearchParams(periodToSearchParams(next), { replace: true });

  const chartProps: DashboardChartProps = {
    series: dashboard?.series ?? [],
    step: dashboard?.step ?? "DAY",
    loading: dashboardLoading,
    error: dashboardError,
    onRetry: retryDashboard,
  };

  return (
    <Stack spacing={3}>
      <DashboardHeader
        caption={formatPeriodCaption(
          period,
          // Только свежий ответ: previousData относится к прошлому периоду.
          dashboardQuery.data?.adminDashboard.dateFrom ?? null
        )}
        picker={
          <PeriodPicker
            period={period}
            today={today}
            onChange={onPeriodChange}
          />
        }
      />

      <OpenSupportRequestsCard />

      <FinancialSummaryCard
        groups={ready ? summaryGroups : null}
        hasComparison={Boolean(dashboard?.previous)}
        loading={anyLoading}
        error={anyError}
        refreshing={refreshing}
        onRetry={retryAll}
      />

      <MetricCards
        cards={ready ? cards : null}
        loading={anyLoading}
        error={anyError}
        onRetry={retryAll}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SalesRevenueShareChart {...chartProps} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MoneyChart {...chartProps} />
        </Grid>
        <Grid size={12}>
          <ViewsChart {...chartProps} />
        </Grid>
      </Grid>

      <OrganizationsTable
        rows={dashboard?.organizations ?? []}
        totalRevenueShareUSD={dashboard?.current.revenueShareUSD ?? 0}
        hasComparison={Boolean(dashboard?.previous)}
        loading={dashboardLoading}
        error={dashboardError}
        onRetry={retryDashboard}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <UnpaidInvoicesTable
            rows={unpaidRows}
            loading={unpaidQuery.loading && !unpaidQuery.data}
            error={Boolean(unpaidQuery.error)}
            onRetry={retryUnpaid}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ExpensesByCategoryTable
            rows={dashboard?.expensesByCategory ?? []}
            range={period.range}
            loading={dashboardLoading}
            error={dashboardError}
            onRetry={retryDashboard}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
